import type { PublicStatement } from "@nmt/shared";
import { Fragment } from "react";
import { RichText } from "./rich-text";
import { formatCents, formatDate } from "./ui";

const qty = (q: number, rateCents: number) => `${q.toLocaleString("en-US", { maximumFractionDigits: 4 })} × ${formatCents(rateCents)}`;

/** A statement as the customer sees it: lines under each invoice's number, totals as chosen, the balance. */
export function StatementView({ s, pdfHref }: { s: PublicStatement; pdfHref?: string }) {
  const o = s.options;
  const settled = s.body.paidCents + s.body.creditedCents;
  return (
    <section className="rounded-[var(--radius-panel)] bg-white px-5 py-4 text-sm shadow-[var(--shadow-card)]">
      <table className="w-full">
        <tbody>
          {s.body.groups.map((g) => (
            <Fragment key={g.id}>
              <tr className="bg-mist/70">
                <td className="px-2 py-1.5 font-semibold" colSpan={o.lineAmounts ? 1 : 2}>
                  Invoice {g.number}
                  {g.title && <span className="font-normal text-ink-soft"> · {g.title}</span>}
                  {o.dates && (g.date || g.dueDate) && (
                    <span className="ml-2 text-xs font-normal text-muted">
                      {[g.date ? `Dated ${formatDate(g.date)}` : "", g.dueDate ? `Due ${formatDate(g.dueDate)}` : ""].filter(Boolean).join(" · ")}
                    </span>
                  )}
                </td>
                {o.lineAmounts && <td className="px-2 py-1.5 text-right text-xs text-muted">{g.status === "paid" ? "Paid" : ""}</td>}
              </tr>
              {g.lines.map((l, i) => (
                <tr key={i} className="border-b border-mist align-top">
                  <td className="py-2 pl-4 whitespace-pre-wrap">
                    {l.description}
                    {o.quantities && l.quantity !== undefined && l.unitPriceCents !== undefined && <div className="text-xs text-muted">{qty(l.quantity, l.unitPriceCents)}</div>}
                  </td>
                  {o.lineAmounts && <td className="py-2 text-right whitespace-nowrap">{formatCents(l.amountCents)}</td>}
                </tr>
              ))}
              {o.invoiceTotals && (
                <tr>
                  <td colSpan={2} className="pt-1 pb-3">
                    <div className="ml-auto flex max-w-xs flex-col gap-0.5 text-xs">
                      <div className="flex justify-between">
                        <span>Invoice {g.number} total</span>
                        <span>{formatCents(g.totalCents)}</span>
                      </div>
                      {g.paidCents + g.creditedCents > 0 && (
                        <div className="flex justify-between text-muted">
                          <span>{g.creditedCents ? "Paid and credited" : "Paid"}</span>
                          <span>−{formatCents(g.paidCents + g.creditedCents)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-semibold">
                        <span>Balance</span>
                        <span>{formatCents(g.balanceCents)}</span>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
          <tr className="border-t-2 border-ink">
            <td className="pt-2">Total of these invoices</td>
            <td className="pt-2 text-right">{formatCents(s.body.totalCents)}</td>
          </tr>
          {settled > 0 && (
            <tr>
              <td className="py-1 text-muted">{s.body.creditedCents ? "Paid and credited" : "Paid"}</td>
              <td className="py-1 text-right text-muted">−{formatCents(settled)}</td>
            </tr>
          )}
          <tr className="text-base font-semibold">
            <td className="py-2">Amount due</td>
            <td className="py-2 text-right">{formatCents(s.body.balanceCents)}</td>
          </tr>
        </tbody>
      </table>
      {o.memo && <RichText className="mt-2 text-ink-soft" text={o.memo} />}
      {pdfHref && (
        <a href={pdfHref} target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm font-semibold text-brand hover:underline">
          Download PDF
        </a>
      )}
    </section>
  );
}
